import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, ArrowUpRight, Heart, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getEvents, markEventInterested } from '../api/api';

const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(`#${id}`, { offset: -72 });
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }
};

const formatDate = (dateStr) => {
  const date = new Date(`${dateStr}T12:00:00`);
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
    day: date.toLocaleDateString('en-US', { day: 'numeric' }),
    month: date.toLocaleDateString('en-US', { month: 'short' })
  };
};

const EventCard = ({ event, index }) => {
  const { weekday, day, month } = formatDate(event.date);
  const [interested, setInterested] = useState(
    () => localStorage.getItem(`vibe_interested_${event.id}`) === '1'
  );
  const [count, setCount] = useState(event.interestedCount || 0);
  const [pending, setPending] = useState(false);

  const handleInterest = async () => {
    if (interested || pending) return;
    setPending(true);
    try {
      const res = await markEventInterested(event.id);
      setCount(res.interestedCount);
      setInterested(true);
      localStorage.setItem(`vibe_interested_${event.id}`, '1');
      toast.success("You're on the vibe list!", {
        description: `Thanks for your interest in ${event.title}. See you there!`
      });
    } catch (err) {
      toast.error('Something went wrong', { description: 'Please try again in a moment.' });
    } finally {
      setPending(false);
    }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (index % 4) * 0.08 }}
      data-testid={`event-card-${event.id}`}
      className="group overflow-hidden rounded-2xl border border-[#f4ede3]/10 bg-[#1d1712] transition-colors duration-300 hover:border-[#f2a33c]/50"
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={event.image}
          alt={event.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1d1712] via-transparent to-transparent" />
        <div className="absolute left-4 top-4 rounded-xl bg-[#14100d]/85 px-3 py-2 text-center backdrop-blur-sm">
          <div className="font-body text-[10px] font-semibold uppercase tracking-[0.2em] text-[#f2a33c]">{weekday}</div>
          <div className="font-display text-2xl font-bold leading-none text-[#f4ede3]">{day}</div>
          <div className="font-body text-[10px] uppercase tracking-[0.2em] text-[#b8ac9c]">{month}</div>
        </div>
        {count > 0 && (
          <div
            data-testid={`event-interest-count-${event.id}`}
            className="font-body absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-[#f2a33c] px-3 py-1 text-[11px] font-semibold text-[#14100d]"
          >
            <Heart size={11} fill="currentColor" />
            {count} interested
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl font-semibold leading-tight text-[#f4ede3]">{event.title}</h3>
        <div className="font-body mt-2 flex items-center gap-2 text-sm text-[#f2a33c]">
          <Clock size={14} />
          <span>{event.time}</span>
        </div>
        <p className="font-body mt-3 line-clamp-2 text-sm leading-relaxed text-[#b8ac9c]">{event.description}</p>
        <button
          data-testid={`event-interest-button-${event.id}`}
          onClick={handleInterest}
          disabled={interested || pending}
          className={`font-body mt-4 flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-[13px] font-semibold uppercase tracking-[0.12em] transition-all duration-300 ${
            interested
              ? 'cursor-default bg-[#f2a33c]/15 text-[#f2a33c]'
              : 'border border-[#f2a33c]/50 text-[#f2a33c] hover:bg-[#f2a33c] hover:text-[#14100d]'
          } disabled:opacity-80`}
        >
          {interested ? (
            <>
              <Check size={15} />
              You're interested
            </>
          ) : pending ? (
            'Saving...'
          ) : (
            <>
              <Heart size={15} />
              I'm Interested
            </>
          )}
        </button>
      </div>
    </motion.article>
  );
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCalendarGraphic, setShowCalendarGraphic] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await getEvents();
        setEvents(data);
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <section id="events" data-testid="events-section" className="bg-[#14100d] px-6 py-24 sm:px-12 sm:py-32">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <p className="font-body text-[11px] font-medium uppercase tracking-[0.45em] text-[#f2a33c]">
            The Calendar
          </p>
          <h2 className="font-display mt-4 text-[clamp(2.6rem,6vw,4.5rem)] font-semibold leading-[1.02] tracking-tight text-[#f4ede3]">
            What's on at <span className="italic text-[#f2a33c]">the Vibe</span>
          </h2>
          <p className="font-body mt-5 text-base leading-relaxed text-[#b8ac9c] sm:text-lg">
            Check out our daily happenings. Interested in hosting a private or community event,
            workshop, or class?{' '}
            <a href="mailto:thevibe249@gmail.com" className="text-[#f2a33c] underline underline-offset-4 hover:text-[#ff9a40]">
              Contact us
            </a>{' '}
            to schedule a tour and let's plan your vision.
          </p>
        </motion.div>

        {/* Featured calendar graphic (hidden until the image is uploaded) */}
        {showCalendarGraphic && (
          <motion.div
            initial={{ opacity: 0, clipPath: 'inset(6% 6% 6% 6%)' }}
            whileInView={{ opacity: 1, clipPath: 'inset(0% 0% 0% 0%)' }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="frame-spotlight mt-14 overflow-hidden rounded-2xl border border-[#f4ede3]/10"
          >
            <img
              data-testid="events-calendar-graphic"
              src="/images/september-calendar.png"
              alt="Vibe Cafe September 2026 Weekly Vibes Calendar"
              loading="lazy"
              onError={() => setShowCalendarGraphic(false)}
              className="w-full object-cover"
            />
          </motion.div>
        )}

        {/* Events grid */}
        {loading ? (
          <p data-testid="events-loading" className="font-body mt-16 text-center text-[#b8ac9c]">Loading events...</p>
        ) : error ? (
          <p data-testid="events-error" className="font-body mt-16 text-center text-red-400">{error}</p>
        ) : events.length > 0 ? (
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {events.map((event, index) => (
              <EventCard key={event.id} event={event} index={index} />
            ))}
          </div>
        ) : (
          <p data-testid="events-empty" className="font-body mt-16 text-center text-[#b8ac9c]">
            No upcoming events at the moment. Check back soon!
          </p>
        )}

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="font-body text-lg text-[#b8ac9c]">Want to host an event at Vibe Cafe?</p>
          <button
            data-testid="events-inquire-button"
            onClick={() => scrollToId('booking')}
            className="btn-pill font-body group mt-5"
          >
            Inquire About Space Rental
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Events;
